import React from "react";
import { Box, Drawer, Radio, Typography, useTheme } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { setReduxSelectedFile } from "redux/appSlice";
import { RequestFile } from "models/interfaces";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import FeatureKeys from "global/constants/FeatureKeys";
import {
  useDocumentUpload,
  ACCEPTED_FILE_EXTENSIONS,
} from "../hooks/useDocumentUpload";

interface MobileDocumentsSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional: called with the picked file after redux is updated. Used by the
   * participant signing screen to also switch the rendered document (its viewer
   * is driven by parent state, not redux). Omit to keep editor behavior. */
  onSelectFile?: (file: RequestFile) => void;
}

const humanizeSize = (bytes?: number) => {
  if (!bytes || Number.isNaN(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const MobileDocumentsSheet: React.FC<MobileDocumentsSheetProps> = ({
  open,
  onClose,
  onSelectFile,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const { signRequest, selectedFile } = useAppSelector(
    (s: RootState) => s.app,
  );

  const {
    fileInputRef,
    isMultiDocAvailable,
    handleUploadClick,
    handleFileInputChange,
    upgradeOpen,
    setUpgradeOpen,
    maintenanceOpen,
    setMaintenanceOpen,
    maintenanceMsg,
    // The signer only lists/selects documents here — never uploads — so skip
    // the sender-only `/secure/document/upload/limit` call.
  } = useDocumentUpload({ skipLimit: true });

  const files: RequestFile[] = signRequest.files ?? [];
  const selectedId = selectedFile?.unsignedDoc || selectedFile?.unsignedPdf;

  const selectFile = (file: any) => {
    dispatch(
      setReduxSelectedFile({
        ...file,
        unsignedDoc: file.unsignedDoc || file.unsignedPdf || "",
        unsignedDocUrl: file.unsignedDocUrl || file.unsignedPdfUrl || "",
      }),
    );
    onSelectFile?.(file);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "documents-sheet-title",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={isMultiDocAvailable}
        accept={ACCEPTED_FILE_EXTENSIONS.join(",")}
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />

      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          id="documents-sheet-title"
          component="h2"
          sx={{ fontSize: "1.125rem", fontWeight: 700, color: m.base[900] }}
        >
          Documents
        </Typography>
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: m.grey[400] }}
        >
          {files.length} Doc
        </Typography>
      </Box>

      <Typography
        sx={{ fontSize: "0.75rem", color: m.grey[400], fontWeight: 500, mb: 2 }}
      >
        Each signer’s fields get a unique color
      </Typography>

      {/* Document list */}
      {files.map((file: RequestFile, index: number) => {
        const id = file.unsignedDoc || file.unsignedPdf;
        const isSelected = id === selectedId;
        return (
          <Box
            key={id || index}
            onClick={() => selectFile(file)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: `1px solid ${m.surface[100]}`,
              borderRadius: "14px",
              p: 1.5,
              mb: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: m.ePurple[50],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon
                sx={{ fontSize: 20, color: m.ePurple[500] }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: m.base[900],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.unsignedDoc || file.unsignedPdf}
              </Typography>
              <Typography
                sx={{ fontSize: "0.6875rem", fontWeight: 500, color: m.grey[400] }}
              >
                {humanizeSize((file as any).size) || "PDF document"}
              </Typography>
            </Box>
            <Radio
              checked={isSelected}
              onChange={() => selectFile(file)}
              onClick={(e) => e.stopPropagation()}
              name="mobile-document-select"
              inputProps={{
                "aria-label": `Select ${
                  file.unsignedDoc || file.unsignedPdf || "document"
                }`,
              }}
              sx={{
                p: 0,
                color: m.grey[50],
                "&.Mui-checked": { color: m.ePurple[500] },
              }}
            />
          </Box>
        );
      })}

      {/* Add Documents — commented out for now. */}
      {/* <Box
        onClick={handleUploadClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          border: `1.5px dashed ${m.ePurple[500]}`,
          borderRadius: "14px",
          py: 1.5,
          mt: 0.5,
          cursor: "pointer",
          color: m.base[500],
        }}
      >
        <AddIcon sx={{ fontSize: 20, color: m.ePurple[500] }} />
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
          Add Documents
        </Typography>
      </Box> */}

      <UpgradeModal
        open={upgradeOpen}
        featureKey={FeatureKeys.ENVELOPE_MULTI_DOCUMENT}
        onClose={() => setUpgradeOpen(false)}
      />
      <MaintenanceBanner
        open={maintenanceOpen}
        message={maintenanceMsg}
        onClose={() => setMaintenanceOpen(false)}
      />
    </Drawer>
  );
};

export default MobileDocumentsSheet;
